export type ArtifactMediaConstraintKey = 'artifactImage' | 'artifactAudio';

export type ArtifactMediaValidationErrorCode = 'unsupported_type' | 'file_too_large';

type ArtifactMediaConstraint = {
  accept: string;
  capture?: string;
  maxFileSize: number;
  allowedExtensions: string[];
  allowedMimePrefixes: string[];
};

const MIME_TYPES_ALLOWED_BY_EXTENSION_FALLBACK = ['application/octet-stream', 'binary/octet-stream'];

export const ARTIFACT_MEDIA_CONSTRAINTS: Record<ArtifactMediaConstraintKey, ArtifactMediaConstraint> = {
  artifactImage: {
    accept: 'image/png,image/jpeg,image/webp,image/heic,image/heif',
    capture: 'environment',
    maxFileSize: 20 * 1024 * 1024,
    allowedExtensions: ['png', 'jpg', 'jpeg', 'webp', 'heic', 'heif'],
    allowedMimePrefixes: ['image/png', 'image/jpeg', 'image/webp', 'image/heic', 'image/heif']
  },
  artifactAudio: {
    accept: 'audio/*,.m4a,.mp4',
    maxFileSize: 100 * 1024 * 1024,
    allowedExtensions: ['wav', 'mp3', 'm4a', 'aac', 'ogg', 'oga', 'webm', 'mp4'],
    allowedMimePrefixes: ['audio/', 'video/mp4', 'application/mp4']
  }
};

const getFileExtension = (fileName: string): string => fileName.split('.').pop()?.toLowerCase() || '';

const isMimeAllowed = (mimeType: string, allowedMimePrefixes: string[]): boolean => {
  if (!mimeType) {
    return false;
  }

  const normalizedMimeType = mimeType.toLowerCase();
  return allowedMimePrefixes.some((allowedMimePrefix) => {
    const normalizedPrefix = allowedMimePrefix.toLowerCase();
    return normalizedPrefix.endsWith('/')
      ? normalizedMimeType.startsWith(normalizedPrefix)
      : normalizedMimeType === normalizedPrefix;
  });
};

export const getArtifactMediaConstraint = (key: ArtifactMediaConstraintKey): ArtifactMediaConstraint => {
  return ARTIFACT_MEDIA_CONSTRAINTS[key];
};

export const validateArtifactMediaFile = async (
  file: File,
  key: ArtifactMediaConstraintKey
): Promise<{ isValid: true } | { isValid: false; errorCode: ArtifactMediaValidationErrorCode }> => {
  const constraint = ARTIFACT_MEDIA_CONSTRAINTS[key];
  const extension = getFileExtension(file.name);
  const normalizedMimeType = file.type.split(';')[0].trim().toLowerCase();
  const hasAllowedExtension = constraint.allowedExtensions.includes(extension);
  const canUseExtensionFallback = MIME_TYPES_ALLOWED_BY_EXTENSION_FALLBACK.includes(normalizedMimeType);

  const isAllowedType = normalizedMimeType
    ? isMimeAllowed(normalizedMimeType, constraint.allowedMimePrefixes) || (canUseExtensionFallback && hasAllowedExtension)
    : hasAllowedExtension;
  if (!isAllowedType) {
    return { isValid: false, errorCode: 'unsupported_type' };
  }

  if (file.size > constraint.maxFileSize) {
    return { isValid: false, errorCode: 'file_too_large' };
  }

  return { isValid: true };
};